import {
  GoalOverviewRoadmapViewOptions,
  TimelineTimeframes,
  Timestamp,
} from '@griply/types';
import Typography from '@mui/material/Typography';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Table14, Timeline214 } from 'shared/assets/icons';
import { TopBarBase } from 'shared/components/connected/top-bar-base';
import { Option, SliderButtons } from 'shared/components/ui/slider-buttons';
import { TimeframeNavigationButtons } from 'shared/components/ui/timeframe-navigation-buttons';

import { ExtraOptionsMenu } from './extra-options-menu';
import { GoalsFilterButton } from './goals-filter-button';
import { HeaderButton } from './header-button';
import * as Styled from './page-top-bar.style';

export type PageTopBarProps = {
  selectedView: GoalOverviewRoadmapViewOptions;
  onChangeView: (view: GoalOverviewRoadmapViewOptions) => void;

  timeframe: TimelineTimeframes;
  onMonth: () => void;
  onQuarter: () => void;
  onYear: () => void;
  onFiveYears: () => void;

  referenceDate: Timestamp;
  onPrev: () => void;
  onToday: () => void;
  onNext: () => void;
};

export const PageTopBar: React.FC<PageTopBarProps> = ({
  selectedView,
  onChangeView,

  timeframe,
  onMonth,
  onQuarter,
  onYear,
  onFiveYears,

  onPrev,
  onToday,
  onNext,
}) => {
  const { t } = useTranslation();

  const viewOptions = useMemo<Option<GoalOverviewRoadmapViewOptions>[][]>(
    () => [
      [
        {
          icon: Table14,
          value: GoalOverviewRoadmapViewOptions.List,
        },
        {
          icon: Timeline214,
          value: GoalOverviewRoadmapViewOptions.Timeline,
        },
      ],
    ],
    [],
  );

  const timeframeOptions = useMemo<Option<TimelineTimeframes>[][]>(
    () => [
      [
        {
          label: t('general.labels.timeframes.month.short'),
          value: TimelineTimeframes.Month,
        },
        {
          label: t('general.labels.timeframes.quarter.short'),
          value: TimelineTimeframes.Quarter,
        },
        {
          label: t('general.labels.timeframes.year.short'),
          value: TimelineTimeframes.Year,
        },
        {
          label: t('general.labels.timeframes.five-years.short'),
          value: TimelineTimeframes.FiveYears,
        },
      ],
    ],
    [t],
  );

  const _onTimelineOption = (value: TimelineTimeframes) => {
    switch (value) {
      case TimelineTimeframes.Month:
        return onMonth();
      case TimelineTimeframes.Quarter:
        return onQuarter();
      case TimelineTimeframes.Year:
        return onYear();
      case TimelineTimeframes.FiveYears:
        return onFiveYears();
    }
  };

  return (
    <TopBarBase>
      <Styled.Container>
        <Styled.Title>
          <Typography variant="h5" component="h1">
            {t('pages.goals.title')}
          </Typography>
        </Styled.Title>

        <Styled.CenterInteractions>
          <SliderButtons
            optionGroups={viewOptions}
            onOption={onChangeView}
            value={selectedView}
            preventDrag
          />
          {selectedView === GoalOverviewRoadmapViewOptions.Timeline && (
            <>
              <SliderButtons
                optionGroups={timeframeOptions}
                onOption={_onTimelineOption}
                value={timeframe}
                preventDrag
              />

              <TimeframeNavigationButtons
                onPrev={onPrev}
                onToday={onToday}
                onNext={onNext}
              />
            </>
          )}
        </Styled.CenterInteractions>

        <Styled.Interactions>
          <GoalsFilterButton />
          <ExtraOptionsMenu />
          <HeaderButton />
        </Styled.Interactions>
      </Styled.Container>
    </TopBarBase>
  );
};
